import { Chip, ToggleButton, Typography, useTheme } from "@mui/material";
import { ReactComponent as MSTeamsIcon } from "../Settings/Microsoft_Office_Teams.svg";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "../../app/helpers";
import { setInputs } from "../../features/Booking-Form/slices/booking.slice";
import { OnlineMeetingProvider } from "../Settings/typings/calendarSync-integration.type";
import { RootState } from "../../app/rootReducer";
import { useEffect } from "react";

type Props = {
  enableEdit: boolean;
};

const TEAMS_PURPLE = "#6264A7";
const TEAMS_PURPLE_DARK = "#464EB8";

const selectedTeamsToggleSx = {
  bgcolor: TEAMS_PURPLE,
  borderColor: TEAMS_PURPLE,
  boxShadow: `inset 0 0 0 1px ${TEAMS_PURPLE}`,
  color: "#fff",
  fontWeight: 600,
  "&:hover": {
    bgcolor: TEAMS_PURPLE_DARK,
    borderColor: TEAMS_PURPLE_DARK
  }
} as const;

export default function MsTeamsCallSwitch({ enableEdit }: Readonly<Props>) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const theme = useTheme();

  const {
    inputs: { onlineMeetingProvider }
  } = useSelector((state: RootState) => state.booking);
  const {
    userInformation: { msGraphEnabled }
  } = useSelector((state: RootState) => state.login);

  const checkedValMsTeams = onlineMeetingProvider
    ? onlineMeetingProvider === OnlineMeetingProvider.MS_TEAMS
    : false;

  useEffect(() => {
    // by default, include online meeting provider MS Teams
    if (msGraphEnabled && onlineMeetingProvider === undefined) {
      dispatch(setInputs({ onlineMeetingProvider: OnlineMeetingProvider.MS_TEAMS }));
    }
  }, [msGraphEnabled]);

  const teamsControlHeightSx = {
    boxSizing: "border-box",
    height: "56px"
  } as const;

  const teamsIcon = <MSTeamsIcon style={{ flexShrink: 0, height: 20, width: 20 }} />;

  if (!enableEdit) {
    if (!checkedValMsTeams) {
      return null;
    }

    return (
      <Chip
        data-testid="booking-conference-msTeamsCallsSwitch"
        icon={teamsIcon}
        label={t("Teams Meeting")}
        sx={{
          ...selectedTeamsToggleSx,
          ...teamsControlHeightSx,
          width: { xs: "100%", sm: "auto" },
          "& .MuiChip-icon": { color: "inherit", ml: 1 },
          "& .MuiChip-label": { fontWeight: 600, px: 1.25 }
        }}
      />
    );
  }

  return (
    <ToggleButton
      aria-label={t("Teams Meeting")}
      data-testid="booking-conference-msTeamsCallsSwitch"
      onChange={() => {
        dispatch(
          setInputs({
            onlineMeetingProvider: checkedValMsTeams ? null : OnlineMeetingProvider.MS_TEAMS
          })
        );
      }}
      selected={checkedValMsTeams}
      sx={{
        ...teamsControlHeightSx,
        alignSelf: "stretch",
        borderColor: checkedValMsTeams ? TEAMS_PURPLE : theme.palette.divider,
        borderRadius: 1,
        color: checkedValMsTeams ? "#fff" : theme.palette.text.primary,
        gap: 0.75,
        justifyContent: "center",
        px: 1.25,
        textTransform: "none",
        whiteSpace: "nowrap",
        width: { xs: "100%", sm: "auto" },
        "&.Mui-selected": selectedTeamsToggleSx,
        "&:hover": {
          bgcolor: checkedValMsTeams ? TEAMS_PURPLE_DARK : theme.palette.action.hover,
          borderColor: checkedValMsTeams ? TEAMS_PURPLE_DARK : theme.palette.text.secondary
        }
      }}
      value="msTeams"
    >
      {teamsIcon}
      <Typography
        component="span"
        sx={{ fontWeight: checkedValMsTeams ? 600 : 500 }}
        variant="body2"
      >
        {t("Teams Meeting")}
      </Typography>
    </ToggleButton>
  );
}
